import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProductTierGrid from '../components/ProductTierGrid';
import Dashboard from '../components/Dashboard';
import '../styles/HomePage.css';

interface ProductTier {
  id: string;
  name: string;
  description: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  metadata: Record<string, string>;
}

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [productTiers, setProductTiers] = useState<ProductTier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProductTiers = async () => {
      try {
        const response = await fetch('/api/product-tiers', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        let tiersFromAPI: ProductTier[] = [];
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.tiers) {
            tiersFromAPI = data.tiers;
          }
        }

        // If no tiers are available from API, use mock data for demonstration
        if (tiersFromAPI.length === 0) {
          const mockTiers: ProductTier[] = [
            {
              id: 'starter',
              name: 'Starter',
              description: 'Perfect for individuals and small businesses getting started with phone number procurement.',
              price_id: 'price_starter',
              amount: 999, // $9.99
              currency: 'usd',
              interval: 'month',
              features: [
                '10 number searches per month',
                'Basic area code filtering',
                'Email notifications',
                'Standard support'
              ],
              metadata: {}
            },
            {
              id: 'professional',
              name: 'Professional',
              description: 'Advanced features for growing businesses that need more power and flexibility.',
              price_id: 'price_professional',
              amount: 2999, // $29.99
              currency: 'usd',
              interval: 'month',
              features: [
                '100 number searches per month',
                'Advanced filtering & patterns',
                'Automated provisioning',
                'Real-time notifications',
                'Priority support',
                'Custom area codes'
              ],
              metadata: { popular: 'true' }
            },
            {
              id: 'enterprise',
              name: 'Enterprise',
              description: 'Unlimited power for large organizations with enterprise-grade requirements.',
              price_id: 'price_enterprise',
              amount: 9999, // $99.99
              currency: 'usd',
              interval: 'month',
              features: [
                'Unlimited number searches',
                'Bulk number provisioning',
                'API access',
                'Custom integrations',
                'Dedicated account manager',
                'SLA guarantee',
                'Advanced analytics'
              ],
              metadata: {}
            }
          ];
          setProductTiers(mockTiers);
        } else {
          setProductTiers(tiersFromAPI);
        }
      } catch (err) {
        console.error('Error fetching product tiers:', err);
        // Set empty array on error to be handled consistently
        setProductTiers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProductTiers();
  }, []);

  const handlePlanSelect = (tier: ProductTier) => {
    // On home page, redirect to registration/login instead of plan selection
    if (user) {
      // If user is logged in, go to account page
      navigate('/account');
    } else {
      // If not logged in, go to enhanced registration page with plan selection
      navigate('/register');
    }
  };

  return (
    <div className="homepage">
      {user ? (
        // Authenticated user - show dashboard
        <Dashboard />
      ) : (
        // Unauthenticated user - show marketing content
        <>
          {/* Hero Section */}
          <div className="hero-section">
            <Container>
              <Row className="justify-content-center">
                <Col xs={12} lg={10}>
                  <div className="hero-content">
                    <div className="hero-logo">
                      <img 
                        src="/pouncer.png"
                        alt="Pouncer Logo"
                        style={{
                          maxWidth: '200px',
                          height: 'auto',
                          maxHeight: '120px'
                        }}
                      />
                    </div>
                    
                    <h1 className="hero-title">
                      Welcome to Pouncer
                    </h1>
                    <p className="hero-subtitle">
                      Locate and Procure desirable phone numbers with ease! And ruthless efficiency!
                    </p>
                    <p className="hero-description">
                      We can automatically detect your desired numbers, area codes or prefixes becoming available and can even automatically provision numbers for you! Transform your business communications with our intelligent phone number procurement platform.
                    </p>
                    
                    <div className="hero-cta">
                      <Link to="/register" className="cta-button">
                        Get Started Today
                      </Link>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>

          {/* Features Section */}
          <div className="features-section">
            <Container>
              <Row>
                <Col xs={12}>
                  <h2 className="section-title">Powerful Phone Number Procurement</h2>
                  <p className="section-subtitle">
                    Discover why businesses choose Pouncer for their phone number acquisition needs
                  </p>
                </Col>
              </Row>
              
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">🔍</div>
                  <h3 className="feature-title">Smart Search</h3>
                  <p className="feature-description">
                    Advanced algorithms automatically detect and identify desirable phone numbers based on your criteria, patterns, and preferences.
                  </p>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon">⚡</div>
                  <h3 className="feature-title">Instant Provisioning</h3>
                  <p className="feature-description">
                    Automatically provision numbers as soon as they become available. No manual intervention required - Pouncer works 24/7.
                  </p>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon">🎯</div>
                  <h3 className="feature-title">Precise Targeting</h3>
                  <p className="feature-description">
                    Target specific area codes, prefixes, patterns, or memorable number sequences. Get exactly what you need for your business.
                  </p>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon">📱</div>
                  <h3 className="feature-title">Smart Notifications</h3>
                  <p className="feature-description">
                    Get instant alerts when desired numbers become available. Never miss an opportunity with our intelligent notification system.
                  </p>
                </div>
              </div>
            </Container>
          </div>

          {/* How It Works Section */}
          <div className="how-it-works-section">
            <Container>
              <Row>
                <Col xs={12}>
                  <h2 className="section-title">How Pouncer Works</h2>
                  <p className="section-subtitle">
                    Simple, automated, and ruthlessly efficient
                  </p>
                </Col>
              </Row>
              
              <div className="steps-container">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3 className="step-title">Define Your Criteria</h3>
                    <p className="step-description">
                      Set up your search parameters including area codes, prefixes, patterns, and any specific requirements for your ideal phone numbers.
                    </p>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3 className="step-title">AI-Powered Monitoring</h3>
                    <p className="step-description">
                      Our intelligent algorithms continuously monitor telecommunications databases and registries for numbers matching your criteria.
                    </p>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h3 className="step-title">Instant Acquisition</h3>
                    <p className="step-description">
                      When a matching number becomes available, Pouncer automatically secures it for you - faster than any manual process could achieve.
                    </p>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h3 className="step-title">Seamless Delivery</h3>
                    <p className="step-description">
                      Get notified immediately and receive your new number with all the documentation and setup assistance you need.
                    </p>
                  </div>
                </div>
              </div>
            </Container>
          </div>

          {/* Pricing Section */}
          {!loading && productTiers.length > 0 && (
            <div className="pricing-section">
              <Container>
                <Row>
                  <Col xs={12}>
                    <h2 className="section-title">Choose Your Plan</h2>
                    <p className="pricing-description">
                      Select the perfect plan for your phone number procurement needs. All plans include our core features with varying levels of capacity and advanced functionality.
                    </p>
                  </Col>
                </Row>
                
                <Row className="justify-content-center mt-4">
                  <Col xs={12}>
                    <ProductTierGrid
                      tiers={productTiers}
                      currentSubscription={null}
                      onSelectPlan={handlePlanSelect}
                      displayOnly={true}
                    />
                  </Col>
                </Row>
              </Container>
            </div>
          )}

          {/* Call to Action Section */}
          <div className="cta-section">
            <Container>
              <Row>
                <Col xs={12}>
                  <div className="cta-content">
                    <h2 className="cta-title">Ready to Get Started?</h2>
                    <p className="cta-text">
                      Start your journey today and experience the power of automated, intelligent number acquisition.
                    </p>
                    <Link to="/register" className="cta-button me-3">
                      Start Free Trial
                    </Link>
                    <Link to="/login" className="cta-button" style={{ background: 'transparent', border: '2px solid white', color: 'white' }}>
                      Sign In
                    </Link>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </>
      )}
    </div>
  );
};

export default HomePage;
